// components
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import Link from '@/components/utilities/SmartLink';
import { Icon } from '@/components/ui/icon';
import VisibleLoading from '@/components/utilities/VisibleLoading';
import ComponentsRender from '@/components/ComponentsRender';

// constants
import { COMPONENTS } from '@/components/components';

// utilities
import { getImagePath } from '@/utils/images';

const HeroLandingBlock = ({
  background,
  title,
  subtitle,
  titleButtons,
  titleContent,
  heroLandingBlockContent: content
}) => {
  return (
    <div data-variant="dark" className="relative w-full">
      {background && (
        <Image
          src={getImagePath(background)}
          alt={background.alternativeText}
          className="-z-10 hidden object-cover xl:block"
          fill={true}
        />
      )}

      <section className="bg-grey-950 relative overflow-x-hidden pt-32 pb-10 text-white 2xl:overflow-x-auto">
        {/* light */}
        <VisibleLoading delay={500} direction="left">
          {({ ref, className }) => (
            <div
              ref={ref}
              className={`gpu-render bg-brand-500 absolute -top-[200px] -left-[200px] hidden h-[427px] w-[427px] rounded-full blur-[245px] lg:block ${className}`}
            />
          )}
        </VisibleLoading>

        <div className="relative xl:container xl:mx-auto">
          {/* lights */}
          <VisibleLoading delay={300} direction="left">
            {({ ref, className }) => (
              <div
                ref={ref}
                className={`gpu-render bg-brand-500 absolute top-[600px] left-[100px] h-[240px] w-[240px] rounded-full blur-[200px] ${className}`}
              />
            )}
          </VisibleLoading>
          <VisibleLoading delay={200} duration={1000} direction="right">
            {({ ref, className }) => (
              <div
                ref={ref}
                className={`gpu-render bg-brand-500 to-brand-400 absolute top-[80px] -right-0 h-[255px] w-[80px] bg-gradient-to-r from-[#cc41e4] blur-[117px] lg:right-30 lg:h-[692px] lg:w-[102px] ${className}`}
              />
            )}
          </VisibleLoading>

          <div className="mx-auto mb-10 w-[90vw] xl:w-(--container-width)">
            <div className="flex flex-row-reverse flex-wrap items-center justify-center gap-10 xl:justify-between">
              <VisibleLoading delay={75}>
                {({ ref, className }) => (
                  <div
                    ref={ref}
                    className={`relative mx-auto h-[286px] w-[228px] shrink-0 lg:h-[490px] lg:w-[390px] lg:scale-100 ${className || ''}`.trim()}
                  >
                    {(titleContent || []).map((component) => (
                      <ComponentsRender
                        key={`${component.id}_${component.__typename}`}
                        type={COMPONENTS}
                        component={component}
                      />
                    ))}
                  </div>
                )}
              </VisibleLoading>

              <div>
                {subtitle && (
                  <VisibleLoading delay={75}>
                    {({ ref, className }) => (
                      <p
                        ref={ref}
                        className={`txt-sm xl:txt-md mb-4 max-w-[250px] text-left font-semibold tracking-widest uppercase lg:leading-loose xl:max-w-full xl:text-center xl:text-left ${className || ''}`.trim()}
                      >
                        {subtitle}
                      </p>
                    )}
                  </VisibleLoading>
                )}

                {title && (
                  <VisibleLoading delay={150}>
                    {({ ref, className }) => (
                      <h1
                        ref={ref}
                        className={`mb-4 max-w-[250px] text-left font-light xl:max-w-[500px] xl:text-center xl:text-left ${className || ''}`.trim()}
                      >
                        <MainClaim>{title}</MainClaim>
                      </h1>
                    )}
                  </VisibleLoading>
                )}

                {(titleButtons || []).length > 0 && (
                  <VisibleLoading delay={300}>
                    {({ ref, className }) => (
                      <div ref={ref} className={`mt-12 flex flex-wrap gap-2 ${className || ''}`.trim()}>
                        {titleButtons.map((button, index) => (
                          <VisibleLoading key={button.id} delay={index * 100}>
                            {({ ref, className }) => (
                              <Button
                                ref={ref}
                                key={button.id}
                                variant={button.variant}
                                size={button.size}
                                asChild={true}
                                className={`basis-full xl:basis-auto ${className || ''}`.trim()}
                              >
                                <Link href={button.url || ''}>
                                  <Icon type={button.icon} /> {button.label}
                                </Link>
                              </Button>
                            )}
                          </VisibleLoading>
                        ))}
                      </div>
                    )}
                  </VisibleLoading>
                )}
              </div>
            </div>

            {(content || []).map((component) => (
              <ComponentsRender
                key={`${component.id}_${component.__typename}`}
                type={COMPONENTS}
                component={component}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
export default HeroLandingBlock;

const MainClaim = ({ children }) => {
  const index = children.indexOf('SMS');

  if (index === -1) {
    return children;
  } else {
    return (
      <>
        {children.substring(0, index)}
        <br />
        <div className="inline-flex h-[28px] w-[28px] translate-y-1 animate-pulse items-center justify-center rounded-full bg-purple-100 lg:h-[55px] lg:w-[55px] lg:-translate-y-2">
          <svg
            width="31"
            height="30"
            viewBox="0 0 31 30"
            className="w-[14px] lg:w-[30px]"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M26.5762 0.690918H4.28649C3.15006 0.692295 2.06056 1.14955 1.25699 1.96239C0.453409 2.77522 0.00136225 3.87727 9.80813e-07 5.02679V28.4405C-0.000242158 28.6031 0.044724 28.7626 0.129766 28.9006C0.214808 29.0386 0.3365 29.1496 0.480946 29.221C0.625087 29.2923 0.786106 29.3212 0.945692 29.3045C1.10528 29.2878 1.25703 29.2261 1.38368 29.1265L8.86703 23.2375H26.5762C27.7127 23.2361 28.8021 22.7788 29.6057 21.966C30.4093 21.1532 30.8614 20.0511 30.8627 18.9016V5.02679C30.8614 3.87727 30.4093 2.77522 29.6057 1.96239C28.8021 1.14955 27.7127 0.692295 26.5762 0.690918ZM15.4314 16.3001H7.71568C7.48831 16.3001 7.27025 16.2087 7.10948 16.0461C6.9487 15.8835 6.85838 15.6629 6.85838 15.4329C6.85838 15.2029 6.9487 14.9823 7.10948 14.8197C7.27025 14.6571 7.48831 14.5657 7.71568 14.5657H15.4314C15.6587 14.5657 15.8768 14.6571 16.0376 14.8197C16.1983 14.9823 16.2887 15.2029 16.2887 15.4329C16.2887 15.6629 16.1983 15.8835 16.0376 16.0461C15.8768 16.2087 15.6587 16.3001 15.4314 16.3001ZM23.147 9.36267H7.71568C7.48831 9.36267 7.27025 9.27131 7.10948 9.10868C6.9487 8.94605 6.85838 8.72548 6.85838 8.49549C6.85838 8.26551 6.9487 8.04494 7.10948 7.88231C7.27025 7.71968 7.48831 7.62832 7.71568 7.62832H23.147C23.3744 7.62832 23.5925 7.71968 23.7532 7.88231C23.914 8.04494 24.0043 8.26551 24.0043 8.49549C24.0043 8.72548 23.914 8.94605 23.7532 9.10868C23.5925 9.27131 23.3744 9.36267 23.147 9.36267Z"
              className="fill-brand-400"
            />
          </svg>
        </div>{' '}
        {children.substring(index)}
      </>
    );
  }
};
